import React, { useEffect, useRef, useState } from "react";
import {
  FaComments,
  FaTimes,
  FaPaperPlane,
  FaMicrophone,
  FaStop,
  FaVolumeUp,
  FaVolumeMute,
} from "react-icons/fa";

import {
  ensureHelpVoicesLoaded,
  isHelpSpeechSupported,
  speakHelpText,
  stopHelpSpeech,
} from "../../helpers/helpTextToSpeech";
import "./SidebarHelpAssistant.css";

const PREVIEW_REPLY =
  "Recebi sua pergunta. Quando a IA estiver ligada, a resposta sai daqui em texto e em áudio. Por enquanto este assistente é só um exemplo de ajuda na plataforma.";

const STARTER_MESSAGES = [
  {
    from: "assistant",
    text:
      "Olá! Eu sou o AgriHelp. Posso orientar no primeiro cadastro, no mapa da fazenda, nos operadores e no restante da plataforma.",
  },
  {
    from: "assistant",
    text:
      "Fale pelo microfone ou escreva sua dúvida. Eu também respondo em áudio. Esta ainda é uma prévia, até a IA ser contratada.",
  },
];

function getSpeechRecognition() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function SidebarHelpAssistant() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState(STARTER_MESSAGES);
  const [listening, setListening] = useState(false);
  const [voiceAnswers, setVoiceAnswers] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const recognitionRef = useRef(null);
  const messagesRef = useRef(null);

  const speechSupported = isHelpSpeechSupported();
  const Recognition = getSpeechRecognition();

  useEffect(() => {
    const cleanupVoices = ensureHelpVoicesLoaded(() => {});
    return () => {
      cleanupVoices();
      stopHelpSpeech();
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, open]);

  function speakReply(text) {
    if (!voiceAnswers || !speechSupported) return;
    speakHelpText(text, {
      onStart: () => setSpeaking(true),
      onEnd: () => setSpeaking(false),
      onError: () => setSpeaking(false),
    });
  }

  function sendQuestion(text) {
    const question = (text || "").trim();
    if (!question) return;

    setMessages((current) => [
      ...current,
      { from: "user", text: question },
      { from: "assistant", text: PREVIEW_REPLY },
    ]);
    setDraft("");
    speakReply(PREVIEW_REPLY);
  }

  function handleSend(event) {
    event.preventDefault();
    sendQuestion(draft);
  }

  function stopListening() {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setListening(false);
  }

  function toggleListening() {
    if (!Recognition) return;
    if (listening) {
      stopListening();
      return;
    }

    stopHelpSpeech();
    setSpeaking(false);

    const recognition = new Recognition();
    recognition.lang = "pt-BR";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const transcript = event.results[0] && event.results[0][0]
        ? event.results[0][0].transcript
        : "";
      setListening(false);
      sendQuestion(transcript);
    };
    recognition.onerror = () => {
      setListening(false);
    };
    recognition.onend = () => {
      setListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }

  function handleClose() {
    stopListening();
    stopHelpSpeech();
    setSpeaking(false);
    setOpen(false);
  }

  function toggleVoiceAnswers() {
    if (voiceAnswers) {
      stopHelpSpeech();
      setSpeaking(false);
    }
    setVoiceAnswers((current) => !current);
  }

  return (
    <div className="sidebar-help-assistant">
      <button
        type="button"
        className="sidebar-help-assistant__trigger"
        onClick={() => setOpen(true)}
      >
        <span className="sidebar-help-assistant__icon">
          <FaComments />
        </span>
        <span className="sidebar-help-assistant__copy">
          <strong>AgriHelp</strong>
          <small>Texto ou áudio para usar a plataforma</small>
        </span>
      </button>

      {open ? (
        <div className="sidebar-help-assistant__panel" role="dialog" aria-modal="true">
          <div className="sidebar-help-assistant__header">
            <div>
              <strong>AgriHelp</strong>
              <small>Fale ou escreva. A resposta também sai em áudio.</small>
            </div>
            <div className="sidebar-help-assistant__header-actions">
              {speechSupported ? (
                <button
                  type="button"
                  className="sidebar-help-assistant__icon-btn"
                  onClick={toggleVoiceAnswers}
                  aria-label={
                    voiceAnswers
                      ? "Desligar resposta em áudio"
                      : "Ligar resposta em áudio"
                  }
                  title={
                    voiceAnswers
                      ? "Resposta em áudio ligada"
                      : "Resposta em áudio desligada"
                  }
                >
                  {voiceAnswers ? <FaVolumeUp /> : <FaVolumeMute />}
                </button>
              ) : null}
              <button
                type="button"
                className="sidebar-help-assistant__close"
                onClick={handleClose}
                aria-label="Fechar"
              >
                <FaTimes />
              </button>
            </div>
          </div>
          <div className="sidebar-help-assistant__messages" ref={messagesRef}>
            {messages.map((message, index) => (
              <p
                key={index}
                className={
                  "sidebar-help-assistant__bubble sidebar-help-assistant__bubble--" +
                  message.from
                }
              >
                {message.text}
              </p>
            ))}
          </div>
          <form className="sidebar-help-assistant__form" onSubmit={handleSend}>
            <input
              type="text"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={
                listening ? "Ouvindo..." : "Escreva ou fale sua dúvida..."
              }
              aria-label="Pergunta para o AgriHelp"
            />
            {Recognition ? (
              <button
                type="button"
                className={
                  "sidebar-help-assistant__mic" +
                  (listening ? " is-listening" : "")
                }
                onClick={toggleListening}
                aria-label={listening ? "Parar gravação" : "Falar a dúvida"}
                title={listening ? "Parar" : "Falar"}
              >
                {listening ? <FaStop /> : <FaMicrophone />}
              </button>
            ) : null}
            <button type="submit" aria-label="Enviar">
              <FaPaperPlane />
            </button>
          </form>
          <p className="sidebar-help-assistant__hint">
            {speaking
              ? "AgriHelp está respondendo em áudio..."
              : "Use o teclado ou o microfone. O AgriHelp responde em texto e em voz."}
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default SidebarHelpAssistant;
